import { Select as AntdSelect, Modal } from "antd";
import {
  Autocomplete,
  Button,
  Input,
  Select,
  Table,
} from "@mantine/core";
import {
  IconBuildingWarehouse,
  IconCircleCheck,
  IconFileText,
  IconHash,
  IconHome,
  IconMapPin,
  IconPhoto,
  IconStack2,
  IconToggleLeft,
  IconUser,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";

import { orpcClient } from "@/lib/orpc-client";
import { single } from "@/lib/orpc-batch";
import { useData } from "@/utility/state/contextAPI";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

const defaultTempArea = {
  name: "",
  enable: true,
  description: "",
  picture: "",
};

const defaultTempStorageLocation = {
  name: "",
  enable: true,
  description: "",
  picture: "",
};

function EditWarehouseListPage() {
  const { t } = useTranslation();
  const [currentStage, setCurrentStage] = useState(0);

  const [employeeData, setEmployeeData] = useState([]);
  const [warehouseData, setWarehouseData] = useState({
    name: "",
    enable: "",
    description: "",
    address: {
      city: "",
      district: "",
      postalCode: "",
      address: "",
    },
    contactId: "",
  });
  const [editWarehouseData, setEditWarehouseData] = useState({});
  const [contactInput, setContactInput] = useState("");

  const [areas, setAreas] = useState([]);

  const [areaModalOpen, setAreaModalOpen] = useState(false);
  const [areaModalMode, setAreaModalMode] = useState("new");
  const [tempArea, setTempArea] = useState(defaultTempArea);

  const [locModalOpen, setLocModalOpen] = useState(false);
  const [locModalMode, setLocModalMode] = useState("new");
  const [locAreaId, setLocAreaId] = useState("");
  const [tempLoc, setTempLoc] = useState(defaultTempStorageLocation);

  const { id } = useParams();
  const {
    modal: { contextOpenModal, contextCloseModal, contextSetModalContent },
  } = useData();
  const navigate = useNavigate();

  const refetchAreas = async () => {
    try {
      const result = await orpcClient.warehouseArea.list({
        filter: { warehouseId: id },
      });
      setAreas(result || []);
    } catch (error) {
      console.error("Error fetching warehouse areas:", error);
    }
  };

  const handleSubmit = async (editWarehouseData) => {
    try {
      const submitData = {
        ...editWarehouseData,
        ...(editWarehouseData.address && {
          address: {
            ...warehouseData.address,
            ...editWarehouseData.address,
            postalCode:
              editWarehouseData.address.postalCode !== undefined
                ? editWarehouseData.address.postalCode === ""
                  ? 0
                  : Number(editWarehouseData.address.postalCode)
                : warehouseData.address?.postalCode || 0,
          },
        }),
      };

      if (Object.keys(submitData).length) {
        await single(
          orpcClient.warehouse.update([
            {
              id: id!,
              ...submitData,
            },
          ])
        );
      }

      setCurrentStage(1);
    } catch (error) {
      console.error("Error in handleSubmit:", error);
    }
  };

  const handleDisplayEnable = (value) => {
    return value === t("warehouse.enabledValue");
  };

  const handleDisplayEnableValue = (enable) => {
    return enable ? t("warehouse.enabledValue") : t("warehouse.disabledValue");
  };

  const handleDataChange = (name, value) => {
    setWarehouseData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    setEditWarehouseData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleChangeAddressData = (type, value) => {
    const processedValue =
      type === "postalCode" ? (value === "" ? "" : Number(value)) : value;

    setWarehouseData((prevData) => ({
      ...prevData,
      address: {
        ...prevData?.address,
        [type]: processedValue,
      },
    }));

    setEditWarehouseData((prevData) => ({
      ...prevData,
      address: {
        ...prevData?.address,
        [type]: processedValue,
      },
    }));
  };

  const handleTempAreaChange = (name, value) => {
    setTempArea((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleTempLocChange = (name, value) => {
    setTempLoc((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleDisplaySelectMembers = (data) => {
    return data?.map((t) => t?.name + ` (ID: ${t?.id})`);
  };

  const handleDisplaySelectMember = (data) => {
    if (!data) return;
    return `${data?.name} (ID: ${data?.id})`;
  };

  const handleContactData = (e) => {
    if (!e) return;

    const match = e?.match(/ID:\s*([^\)]+)/);
    const idNumber = match ? match[1] : null;

    const selectedData = employeeData.find((emp) => emp?.id === idNumber);

    return selectedData?.id;
  };

  const handleAddArea = () => {
    setAreaModalMode("new");
    setTempArea(defaultTempArea);
    setAreaModalOpen(true);
  };

  const handleEditArea = (area) => {
    setAreaModalMode("edit");
    setTempArea({
      id: area.id,
      name: area.name || "",
      enable: area.enable,
      description: area.description || "",
      picture: area.picture || "",
    });
    setAreaModalOpen(true);
  };

  const handleSaveArea = async () => {
    try {
      if (areaModalMode === "new") {
        await single(
          orpcClient.warehouseArea.create([
            {
              warehouseId: id!,
              name: tempArea.name,
              enable: tempArea.enable,
              description: tempArea.description,
              picture: tempArea.picture,
            },
          ])
        );
      } else {
        await single(
          orpcClient.warehouseArea.update([
            {
              id: tempArea.id,
              name: tempArea.name,
              enable: tempArea.enable,
              description: tempArea.description,
              picture: tempArea.picture,
            },
          ])
        );
      }
      setTempArea(defaultTempArea);
      setAreaModalOpen(false);
      await refetchAreas();
    } catch (error) {
      console.error("Error saving warehouse area:", error);
    }
  };

  const handleDeleteArea = (area) => {
    Modal.confirm({
      title: t("warehouse.deleteArea"),
      content: t("warehouse.deleteAreaConfirm"),
      okText: t("warehouse.deleteBtn"),
      cancelText: t("warehouse.cancel"),
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await single(orpcClient.warehouseArea.delete([{ id: area.id }]));
          await refetchAreas();
        } catch (error) {
          console.error("Error deleting warehouse area:", error);
        }
      },
    });
  };

  const handleAddLoc = (areaId) => {
    setLocModalMode("new");
    setLocAreaId(areaId);
    setTempLoc(defaultTempStorageLocation);
    setLocModalOpen(true);
  };

  const handleEditLoc = (areaId, location) => {
    setLocModalMode("edit");
    setLocAreaId(areaId);
    setTempLoc({
      id: location.id,
      name: location.name || "",
      enable: location.enable,
      description: location.description || "",
      picture: location.picture || "",
    });
    setLocModalOpen(true);
  };

  const handleSaveLoc = async () => {
    try {
      if (locModalMode === "new") {
        await single(
          orpcClient.storageLocation.create([
            {
              warehouseAreaId: locAreaId,
              name: tempLoc.name,
              enable: tempLoc.enable,
              description: tempLoc.description,
              picture: tempLoc.picture,
            },
          ])
        );
      } else {
        await single(
          orpcClient.storageLocation.update([
            {
              id: tempLoc.id,
              name: tempLoc.name,
              enable: tempLoc.enable,
              description: tempLoc.description,
              picture: tempLoc.picture,
            },
          ])
        );
      }
      setTempLoc(defaultTempStorageLocation);
      setLocModalOpen(false);
      await refetchAreas();
    } catch (error) {
      console.error("Error saving storage location:", error);
    }
  };

  const handleDeleteLoc = async (areaId, location) => {
    try {
      await single(orpcClient.storageLocation.delete([{ id: location.id }]));
      await refetchAreas();
    } catch (error) {
      console.error("Error deleting storage location:", error);
    }
  };

  const {
    name,
    enable,
    description,
    address: { city, district, postalCode, address: street } = {},
  } = warehouseData || {};

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await single(orpcClient.warehouse.read([{ id: id! }]));
        if (!result) return;
        setWarehouseData(result);

        if (result?.contact) {
          setContactInput(handleDisplaySelectMember(result.contact));
        }
      } catch (error) {
        console.error("Error fetching warehouse data:", error);
      }
    };

    fetchData();
    refetchAreas();
  }, [id]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await orpcClient.employee.list({});
        setEmployeeData(result || []);
      } catch (error) {
        console.error("Error fetching employee data:", error);
      }
    };

    fetchData();
  }, []);

  const renderControl = (label, control, Icon) => (
    <div className="space-y-1.5">
      <label className="flex items-center gap-1.5 text-xs text-gray-500">
        {Icon ? <Icon size={13} /> : null}
        {label}
      </label>
      {control}
    </div>
  );

  return currentStage === 0 ? (
    <div className="min-h-screen p-8">
      <div className="mx-auto max-w-4xl rounded-xl bg-white p-8 space-y-5">
        <div>
          <div className="text-4xl font-bold" style={{ color: "#6F6F6F" }}>
            {name || t("warehouse.edit") || "編輯倉庫"}
          </div>
          <div className="mt-2 text-sm text-gray-500">
            {t("warehouse.edit") || "編輯倉庫"}
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <IconBuildingWarehouse size={16} className="text-blue-600" />
            <h3 className="text-sm font-medium text-gray-700">
              {t("warehouse.basicInfo") || "基本資訊"}
            </h3>
          </div>
          <div className="grid grid-cols-1 gap-4 p-6 md:grid-cols-2 lg:grid-cols-3">
            {renderControl(
              t("warehouse.warehouseName") || "倉庫名稱",
              <Input
                value={name}
                onChange={({ target: { value } }) =>
                  handleDataChange("name", value)
                }
                leftSection={<IconBuildingWarehouse size={16} />}
              />,
              IconBuildingWarehouse
            )}
            {renderControl(
              t("warehouse.enable") || "是否啟用",
              <Select
                value={handleDisplayEnableValue(enable)}
                data={[
                  t("warehouse.enabledValue"),
                  t("warehouse.disabledValue"),
                ]}
                onChange={(value) =>
                  handleDataChange("enable", handleDisplayEnable(value))
                }
                leftSection={<IconToggleLeft size={16} />}
              />,
              IconCircleCheck
            )}
            {renderControl(
              t("warehouse.warehouseDescription") || "倉庫描述",
              <Input
                value={description}
                onChange={({ target: { value } }) =>
                  handleDataChange("description", value)
                }
                leftSection={<IconFileText size={16} />}
              />,
              IconFileText
            )}
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <IconMapPin size={16} className="text-blue-600" />
            <h3 className="text-sm font-medium text-gray-700">
              {t("warehouse.warehouseAddress") || "倉庫地址"}
            </h3>
          </div>
          <div className="grid grid-cols-1 gap-4 p-6 sm:grid-cols-2 lg:grid-cols-4">
            {renderControl(
              t("warehouse.city") || "縣市",
              <Input
                leftSection={<IconMapPin size={16} />}
                value={city}
                onChange={({ target: { value } }) =>
                  handleChangeAddressData("city", value)
                }
              />,
              IconMapPin
            )}
            {renderControl(
              t("warehouse.district") || "行政區",
              <Input
                leftSection={<IconMapPin size={16} />}
                value={district}
                onChange={({ target: { value } }) =>
                  handleChangeAddressData("district", value)
                }
              />,
              IconMapPin
            )}
            {renderControl(
              t("warehouse.postalCode") || "郵遞區號",
              <Input
                leftSection={<IconHash size={16} />}
                value={postalCode?.toString() || ""}
                onChange={({ target: { value } }) =>
                  handleChangeAddressData("postalCode", value)
                }
              />,
              IconHash
            )}
            {renderControl(
              t("warehouse.address") || "地址",
              <Input
                leftSection={<IconHome size={16} />}
                value={street}
                onChange={({ target: { value } }) =>
                  handleChangeAddressData("address", value)
                }
              />,
              IconHome
            )}
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <IconUser size={16} className="text-blue-600" />
            <h3 className="text-sm font-medium text-gray-700">
              {t("warehouse.contactPerson") || "聯絡人"}
            </h3>
          </div>
          <div className="p-6">
            {renderControl(
              t("warehouse.contactPerson") || "聯絡人",
              <Autocomplete
                placeholder={
                  employeeData && employeeData.length > 0
                    ? t("warehouse.selectContact") || "請選擇聯絡人"
                    : t("warehouse.noEmployeeData") || "暫無員工資料可供選擇"
                }
                leftSection={<IconUser size={16} />}
                data={handleDisplaySelectMembers(employeeData)}
                value={contactInput || ""}
                onChange={(value) => setContactInput(value)}
                onOptionSubmit={(e) =>
                  handleDataChange("contactId", handleContactData(e))
                }
                disabled={!employeeData || employeeData.length === 0}
              />,
              IconUser
            )}
          </div>
        </div>

        {/* 儲位管理 section commented out
        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-6 py-4">
            <div className="flex items-center gap-2">
              <IconStack2 size={16} className="text-blue-600" />
              <h3 className="text-sm font-medium text-gray-700">
                {t("warehouse.areaSection")}
              </h3>
            </div>
            <Button
              variant="default"
              radius="md"
              size="sm"
              onClick={handleAddArea}
            >
              {t("warehouse.addArea")}
            </Button>
          </div>
          <div className="space-y-4 p-6">
            {areas && areas.length ? (
              areas.map((area) => (
                <div
                  key={area.id}
                  className="overflow-hidden rounded-lg border border-gray-200"
                >
                  <div className="flex items-center justify-between gap-2 border-b border-gray-100 bg-gray-50/60 px-4 py-3">
                    <div className="flex items-center gap-2">
                      <IconStack2 size={15} className="text-gray-500" />
                      <span className="text-sm font-medium text-gray-700">
                        {area.name}
                      </span>
                      <span className="text-xs text-gray-400">
                        {area.enable
                          ? t("warehouse.enabledValue")
                          : t("warehouse.disabledValue")}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="default"
                        size="xs"
                        radius="md"
                        onClick={() => handleAddLoc(area.id)}
                      >
                        {t("warehouse.addStorage")}
                      </Button>
                      <Button
                        variant="default"
                        size="xs"
                        radius="md"
                        onClick={() => handleEditArea(area)}
                      >
                        {t("warehouse.editBtn")}
                      </Button>
                      <Button
                        size="xs"
                        radius="md"
                        color="red"
                        onClick={() => handleDeleteArea(area)}
                      >
                        {t("warehouse.deleteBtn")}
                      </Button>
                    </div>
                  </div>
                  <div className="p-4">
                    {area.storageLocation && area.storageLocation.length ? (
                      <Table highlightOnHover>
                        <Table.Thead>
                          <Table.Tr style={{ color: "#6F6F6F" }}>
                            <Table.Th>
                              {t("warehouse.storageLocationName") || "儲位名稱"}
                            </Table.Th>
                            <Table.Th>
                              {t("warehouse.enable") || "是否啟用"}
                            </Table.Th>
                            <Table.Th>
                              {t("warehouse.description") || "儲位描述"}
                            </Table.Th>
                            <Table.Th>{t("warehouse.actions") || "操作"}</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {area.storageLocation.map((location) => (
                            <Table.Tr key={location.id}>
                              <Table.Td>{location.name}</Table.Td>
                              <Table.Td>
                                {location.enable
                                  ? t("warehouse.enabledValue")
                                  : t("warehouse.disabledValue")}
                              </Table.Td>
                              <Table.Td>{location.description}</Table.Td>
                              <Table.Td>
                                <div className="flex gap-2">
                                  <Button
                                    variant="default"
                                    size="xs"
                                    radius="md"
                                    onClick={() =>
                                      handleEditLoc(area.id, location)
                                    }
                                  >
                                    {t("warehouse.editBtn") || "編輯"}
                                  </Button>
                                  <Button
                                    size="xs"
                                    radius="md"
                                    color="red"
                                    onClick={() =>
                                      handleDeleteLoc(area.id, location)
                                    }
                                  >
                                    {t("warehouse.deleteBtn") || "刪除"}
                                  </Button>
                                </div>
                              </Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    ) : (
                      <div className="text-center text-xs text-gray-400">
                        {t("warehouse.storageLocationSection")}
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-sm text-gray-400">
                {t("warehouse.noAreaYet")}
              </div>
            )}
          </div>
        </div>
        */}

        <div className="flex gap-4">
          <Button
            variant="default"
            style={{ flex: 1 }}
            radius="md"
            onClick={() => navigate(`/hrMainPage/warehouse/${id}`)}
          >
            {t("warehouse.cancel") || "取消"}
          </Button>
          <Button
            variant="filled"
            color="gray"
            style={{ flex: 1 }}
            radius="md"
            onClick={() => handleSubmit(editWarehouseData)}
          >
            {t("warehouse.save") || "儲存"}
          </Button>
        </div>
      </div>

      {/* 儲位管理 modals commented out
      <Modal
        open={areaModalOpen}
        onOk={handleSaveArea}
        onCancel={() => {
          setTempArea(defaultTempArea);
          setAreaModalOpen(false);
        }}
      >
        <div className="space-y-4">
          <div className="text-2xl font-bold" style={{ color: "#6F6F6F" }}>
            {tempArea?.name || t("warehouse.areaName")}
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {renderControl(
              t("warehouse.areaName"),
              <Input
                value={tempArea?.name}
                onChange={({ target: { value } }) =>
                  handleTempAreaChange("name", value)
                }
                leftSection={<IconStack2 size={16} />}
              />,
              IconStack2
            )}
            {renderControl(
              t("warehouse.enable") || "是否啟用",
              <AntdSelect
                options={[
                  {
                    value: t("warehouse.enabledValue"),
                    label: t("warehouse.enabledValue"),
                  },
                  {
                    value: t("warehouse.disabledValue"),
                    label: t("warehouse.disabledValue"),
                  },
                ]}
                value={
                  tempArea?.enable
                    ? t("warehouse.enabledValue")
                    : t("warehouse.disabledValue")
                }
                onChange={(value) =>
                  handleTempAreaChange("enable", handleDisplayEnable(value))
                }
                style={{ width: "100%" }}
              />,
              IconCircleCheck
            )}
          </div>
          {renderControl(
            t("warehouse.areaDescription"),
            <Input
              value={tempArea?.description}
              onChange={({ target: { value } }) =>
                handleTempAreaChange("description", value)
              }
              leftSection={<IconFileText size={16} />}
            />,
            IconFileText
          )}
          {renderControl(
            t("warehouse.picture"),
            <Input
              value={tempArea?.picture}
              placeholder={t("warehouse.pictureUrlPlaceholder")}
              onChange={({ target: { value } }) =>
                handleTempAreaChange("picture", value)
              }
              leftSection={<IconPhoto size={16} />}
            />,
            IconPhoto
          )}
        </div>
      </Modal>

      <Modal
        open={locModalOpen}
        onOk={handleSaveLoc}
        onCancel={() => {
          setTempLoc(defaultTempStorageLocation);
          setLocModalOpen(false);
        }}
      >
        <div className="space-y-4">
          <div className="text-2xl font-bold" style={{ color: "#6F6F6F" }}>
            {tempLoc?.name || t("warehouse.storageLocationName")}
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {renderControl(
              t("warehouse.storageLocationName") || "儲位名稱",
              <Input
                value={tempLoc?.name}
                onChange={({ target: { value } }) =>
                  handleTempLocChange("name", value)
                }
                leftSection={<IconBuildingWarehouse size={16} />}
              />,
              IconBuildingWarehouse
            )}
            {renderControl(
              t("warehouse.enable") || "是否啟用",
              <AntdSelect
                options={[
                  {
                    value: t("warehouse.enabledValue"),
                    label: t("warehouse.enabledValue"),
                  },
                  {
                    value: t("warehouse.disabledValue"),
                    label: t("warehouse.disabledValue"),
                  },
                ]}
                value={
                  tempLoc?.enable
                    ? t("warehouse.enabledValue")
                    : t("warehouse.disabledValue")
                }
                onChange={(value) =>
                  handleTempLocChange("enable", handleDisplayEnable(value))
                }
                style={{ width: "100%" }}
              />,
              IconCircleCheck
            )}
          </div>
          {renderControl(
            t("warehouse.fieldDescription") || "欄位描述",
            <Input
              value={tempLoc?.description}
              onChange={({ target: { value } }) =>
                handleTempLocChange("description", value)
              }
              leftSection={<IconFileText size={16} />}
            />,
            IconFileText
          )}
          {renderControl(
            t("warehouse.picture"),
            <Input
              value={tempLoc?.picture}
              placeholder={t("warehouse.pictureUrlPlaceholder")}
              onChange={({ target: { value } }) =>
                handleTempLocChange("picture", value)
              }
              leftSection={<IconPhoto size={16} />}
            />,
            IconPhoto
          )}
        </div>
      </Modal>
      */}
    </div>
  ) : (
    <div className="min-h-screen flex h-full items-center justify-center p-14 text-center">
      <div className="w-[487px] rounded-xl bg-white p-8">
        <div style={{ color: "#6F6F6F" }}>編輯倉儲成功！</div>
        <Button
          variant="filled"
          color="gray"
          style={{ width: "100%", margin: "1rem 0" }}
          radius="md"
          onClick={() => navigate("/hrMainPage/warehouse")}
        >
          確定
        </Button>
      </div>
    </div>
  );
}
export default EditWarehouseListPage;
